import { RefreshOutlined } from "@material-ui/icons";
import {
  BusinessOutlined,
  ClearOutlined,
  DownloadOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  FilterAltOutlined,
  InsightsOutlined,
  ListAltOutlined,
  RemoveRedEyeOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PsychologyAltOutlinedIcon from "@mui/icons-material/PsychologyAltOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { DataGrid } from "@mui/x-data-grid";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";
import { userDetailContext } from "../../../App";
import useToast from "../../../hooks/useToast";
import {
  evaluation_refonte_saison_service,
  evaluation_utilitises_service,
} from "../../../services/PersonalEvaluationRefonteService";
import PlanningMissionService from "../../../services/PlanningMissionService";
import UserService from "../../../services/UserService";
import { hasPrivilege } from "../../../utils/privilege";
import LoadingSimpleBackdrop from "../../tools/LoadingSimpleBackdrop";
import DialogChoixFicheEvaluation from "../fiche_evaluation/DialogChoixFicheEvaluation";
import AutocompleteCombobox, {
  adaptDataValueToAutocompleteData,
} from "../shared/AutocompleteCombobox";
import EvaluationExaminationForm from "./EvaluationExaminationForm";
import EvaluationExaminationFormResume from "./EvaluationExaminationFormResume";
import EvaluationPreviewDrawer from "./EvaluationPreviewDrawer";

/* ------------------------------------------------------------------ */
/*  Constantes                                                         */
/* ------------------------------------------------------------------ */
const INITIAL_FILTRE = {
  critere: null,
  direction: {},
  service: {},
  session: {},
  evalued: false,
  mot_cle: "",
  refresh: 0,
};

const DEFAULT_STATS = {
  total: 0,
  autoEvaluationDone: 0,
  evaluationDirecteurDone: 0,
  autoEvaluationPending: 0,
  faible: 0,
  aAmeliorer: 0,
  satisfaisant: 0,
  superieur: 0,
  exceptionnel: 0,
};

const CLASSIFICATION_MAP = {
  faible: "faible",
  "non satisfaisant": "aAmeliorer",
  satisfaisant: "satisfaisant",
  supérieur: "superieur",
  superieur: "superieur",
  bon: "superieur",
  exceptionnel: "exceptionnel",
  excellent: "exceptionnel",
};

const normalizeClassification = (value) => {
  if (!value) return null;
  const key = String(value).trim().toLowerCase();
  return CLASSIFICATION_MAP[key] ?? null;
};

const computeClassificationStats = (list = []) => {
  const counts = {
    faible: 0,
    aAmeliorer: 0,
    satisfaisant: 0,
    superieur: 0,
    exceptionnel: 0,
  };

  list.forEach((item) => {
    const key = normalizeClassification(item?.CLASSIFICATION);
    if (key) counts[key] += 1;
  });

  const totalClassified = Object.values(counts).reduce((a, b) => a + b, 0);
  if (totalClassified === 0) return counts;

  const toPct = (n) => Math.round((n / totalClassified) * 100);
  return {
    faible: toPct(counts.faible),
    aAmeliorer: toPct(counts.aAmeliorer),
    satisfaisant: toPct(counts.satisfaisant),
    superieur: toPct(counts.superieur),
    exceptionnel: toPct(counts.exceptionnel),
  };
};

const mapDirectionStats = (rawDirections = []) =>
  rawDirections.map((d) => ({
    directionId: d.direction_id,
    directionCode: String(d.direction_code ?? "").trim() || "N/A",
    nbrpersonne: Number(d.nbrpersonne) || 0,
    nbrevaluation: Number(d.nbrevaluation) || 0,
    finievaluation: Number(d.finievaluation) || 0,
    autoEvaluationPending: Math.max(
      (Number(d.nbrpersonne) || 0) - (Number(d.nbrevaluation) || 0),
      0,
    ),
  }));

const CHART_COLORS = {
  personne: "#1a73e8",
  evaluation: "#2e7d32",
  pending: "#f39797",
};

const STAT_COLORS = {
  personnel: "#1a73e8",
  evaluationDone: "#2e7d32",
  inProgress: "#68b2dd",
  pending: "#f9a825",
};

/* ------------------------------------------------------------------ */
/*  Composants UI réutilisables                                        */
/* ------------------------------------------------------------------ */
const TextCell = ({ value }) => (
  <Typography
    variant="body2"
    sx={{
      whiteSpace: "normal",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      lineHeight: 1.4,
    }}
  >
    {value ?? "N/A"}
  </Typography>
);

const StatCard = ({ label, value, color, icon, suffix = "" }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2,
      height: "100%",
      borderColor: `${color}88`,
      background: `linear-gradient(135deg, ${color}22 0%, #ffffff 70%)`,
      transition: "transform .15s ease, box-shadow .15s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 4px 12px ${color}55`,
      },
    }}
  >
    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: `${color}33`,
            color: color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color, lineHeight: 1.1, fontSize: 20 }}
          >
            {value}
            {suffix}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", fontSize: 11, lineHeight: 1.2 }}
          >
            {label}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const SectionHeader = ({
  icon,
  title,
  count,
  expanded,
  onToggle,
  color = "#1a73e8",
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ mb: expanded ? 1.5 : 0 }}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ color, display: "flex" }}>{icon}</Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.6,
          fontSize: 11,
        }}
      >
        {title}
      </Typography>
      {typeof count === "number" && (
        <Chip
          size="small"
          label={count}
          sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
        />
      )}
    </Stack>
    <IconButton size="small" onClick={onToggle} sx={{ color: "#64748b" }}>
      {expanded ? (
        <ExpandLessOutlined fontSize="small" />
      ) : (
        <ExpandMoreOutlined fontSize="small" />
      )}
    </IconButton>
  </Stack>
);

/* ------------------------------------------------------------------ */
/*  Panneaux de statistiques                                           */
/* ------------------------------------------------------------------ */
const StatsPanelContent = ({ stats = DEFAULT_STATS }) => {
  const s = { ...DEFAULT_STATS, ...stats };
  const [expanded, setExpanded] = useState(true);

  return (
    <>
      <SectionHeader
        icon={<InsightsOutlined fontSize="small" />}
        title="Statistiques globales"
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
      />

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Nombre de personnels"
              value={s.total}
              color={STAT_COLORS.personnel}
              icon={<PeopleAltOutlinedIcon fontSize="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Auto-évaluations terminées"
              value={s.autoEvaluationDone}
              color={STAT_COLORS.evaluationDone}
              icon={<TaskAltOutlinedIcon fontSize="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="En cours d'approbation"
              value={s.evaluationDirecteurDone}
              color={STAT_COLORS.inProgress}
              icon={<VerifiedOutlinedIcon fontSize="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Auto évaluation non effectuée"
              value={s.autoEvaluationPending}
              color={STAT_COLORS.pending}
              icon={<HourglassEmptyOutlinedIcon fontSize="small" />}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            fontSize: 11,
          }}
        >
          Répartition par appréciation
        </Typography>

        <Grid container spacing={1.5} mt={0.5}>
          <Grid item xs={6} sm={4} md={2.4}>
            <StatCard
              label="Faible"
              value={s.faible}
              suffix="%"
              color="#c62828"
              icon={
                <Typography fontSize={13} fontWeight={700}>
                  F
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <StatCard
              label="Non satisfaisant"
              value={s.aAmeliorer}
              suffix="%"
              color="#ef6c00"
              icon={
                <Typography fontSize={13} fontWeight={700}>
                  A
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <StatCard
              label="Satisfaisant"
              value={s.satisfaisant}
              suffix="%"
              color="#f9a825"
              icon={
                <Typography fontSize={13} fontWeight={700}>
                  S
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <StatCard
              label="Supérieur"
              value={s.superieur}
              suffix="%"
              color="#2e7d32"
              icon={
                <Typography fontSize={13} fontWeight={700}>
                  S+
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <StatCard
              label="Exceptionnel"
              value={s.exceptionnel}
              suffix="%"
              color="#1565c0"
              icon={
                <Typography fontSize={13} fontWeight={700}>
                  E
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </Collapse>
    </>
  );
};

const DirectionStatsContent = ({ directions = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState("stacked");

  const totals = useMemo(
    () =>
      directions.reduce(
        (acc, d) => ({
          nbrpersonne: acc.nbrpersonne + d.nbrpersonne,
          nbrevaluation: acc.nbrevaluation + d.nbrevaluation,
          autoEvaluationPending:
            acc.autoEvaluationPending + d.autoEvaluationPending,
        }),
        { nbrpersonne: 0, nbrevaluation: 0, autoEvaluationPending: 0 },
      ),
    [directions],
  );

  const globalTaux =
    totals.nbrpersonne > 0
      ? Math.round((totals.nbrevaluation / totals.nbrpersonne) * 100)
      : 0;

  const chartData = useMemo(
    () => ({
      labels: directions.map((d) => d.directionCode),
      personnes: directions.map((d) => d.nbrpersonne),
      evaluations: directions.map((d) => d.nbrevaluation),
      pending: directions.map((d) => d.autoEvaluationPending),
    }),
    [directions],
  );

  if (directions.length === 0) return null;

  return (
    <>
      <SectionHeader
        icon={<BusinessOutlined fontSize="small" />}
        title="Statistiques par direction"
        count={directions.length}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        color="#6a1b9a"
      />

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          mb={1.5}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`${totals.nbrpersonne} personnels`}
              sx={{
                bgcolor: "#1a73e814",
                color: "#1a73e8",
                fontWeight: 600,
                fontSize: 11,
              }}
            />
            <Chip
              size="small"
              label={`${totals.nbrevaluation} évaluations`}
              sx={{
                bgcolor: "#2e7d3214",
                color: "#2e7d32",
                fontWeight: 600,
                fontSize: 11,
              }}
            />
            <Chip
              size="small"
              label={`Taux global : ${globalTaux}%`}
              sx={{
                bgcolor: "#6a1b9a14",
                color: "#6a1b9a",
                fontWeight: 600,
                fontSize: 11,
              }}
            />
          </Stack>

          <Stack direction="row" spacing={0.5}>
            <Chip
              size="small"
              label="Empilé"
              onClick={() => setMode("stacked")}
              variant={mode === "stacked" ? "filled" : "outlined"}
              sx={{
                fontSize: 10,
                height: 22,
                fontWeight: 600,
                cursor: "pointer",
              }}
            />
            <Chip
              size="small"
              label="Groupé"
              onClick={() => setMode("grouped")}
              variant={mode === "grouped" ? "filled" : "outlined"}
              sx={{
                fontSize: 10,
                height: 22,
                fontWeight: 600,
                cursor: "pointer",
              }}
            />
          </Stack>
        </Stack>

        <Box sx={{ width: "100%", height: 400 }}>
          <BarChart
            series={
              mode === "stacked"
                ? [
                    {
                      data: chartData.evaluations,
                      label: "Auto-éval. terminées",
                      id: "evaluationsId",
                      color: CHART_COLORS.evaluation,
                      stack: "total",
                    },
                    {
                      data: chartData.pending,
                      label: "En attente",
                      id: "pendingId",
                      color: CHART_COLORS.pending,
                      stack: "total",
                    },
                  ]
                : [
                    {
                      data: chartData.personnes,
                      label: "Personnels",
                      id: "personnesId",
                      color: CHART_COLORS.personne,
                    },
                    {
                      data: chartData.evaluations,
                      label: "Auto-éval. terminées",
                      id: "evaluationsId",
                      color: CHART_COLORS.evaluation,
                    },
                    {
                      data: chartData.pending,
                      label: "En attente",
                      id: "pendingId",
                      color: CHART_COLORS.pending,
                    },
                  ]
            }
            xAxis={[
              {
                data: chartData.labels,
                scaleType: "band",
                height: 44,
                tickLabelStyle: {
                  fontSize: 11,
                  fontWeight: 700,
                  fill: "#334155",
                },
              },
            ]}
            yAxis={[
              {
                width: 50,
                tickLabelStyle: { fontSize: 11, fill: "#64748b" },
              },
            ]}
            margin={{ top: 20, right: 20, bottom: 70, left: 60 }}
            borderRadius={4}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "top", horizontal: "right" },
                padding: 0,
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                markGap: 5,
                itemGap: 15,
                labelStyle: {
                  fontSize: 11,
                  fill: "#475569",
                },
              },
            }}
            sx={{
              "& .MuiChartsAxis-tickLabel": {
                fontSize: 11,
                fill: "#334155",
                fontWeight: 600,
              },
              "& .MuiChartsAxis-line": { stroke: "#e2e8f0" },
              "& .MuiChartsAxis-tick": { stroke: "#e2e8f0" },
              "& .MuiChartsGrid-line": { stroke: "#f1f5f9" },
            }}
          />
        </Box>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          flexWrap="wrap"
          useFlexGap
          mt={1}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 0.5,
                bgcolor: CHART_COLORS.evaluation,
              }}
            />
            <Typography fontSize={11} color="#64748b">
              Terminées
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 0.5,
                bgcolor: CHART_COLORS.pending,
                border: "1px solid #e2c92c",
              }}
            />
            <Typography fontSize={11} color="#64748b">
              En attente
            </Typography>
          </Stack>
          {mode === "grouped" && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 0.5,
                  bgcolor: CHART_COLORS.personne,
                }}
              />
              <Typography fontSize={11} color="#64748b">
                Personnels
              </Typography>
            </Stack>
          )}
        </Stack>
      </Collapse>
    </>
  );
};

export const StatsAndDirectionPanel = ({
  stats = DEFAULT_STATS,
  directions = [],
  loading = false,
  showStats = true,
}) => {
  const hasDirections = directions.length > 0;

  return (
    <Box
      sx={{
        position: "relative",
        mt: 2,
        bgcolor: "#ffffff",
        borderRadius: 2,
        border: "1px solid #e2e8f0",
        px: 2,
        py: 1.5,
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 2,
            zIndex: 3,
            backdropFilter: "blur(1px)",
          }}
        >
          <CircularProgress size={26} />
        </Box>
      )}

      {showStats && <StatsPanelContent stats={stats} />}

      {hasDirections && (
        <>
          {showStats && <Divider sx={{ my: 2 }} />}
          <DirectionStatsContent directions={directions} />
        </>
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/*  Composant principal                                                */
/* ------------------------------------------------------------------ */
const EmployeeListTable = ({ title }) => {
  /* ----------------------------- États ----------------------------- */
  const [filtre, setFiltre] = useState(INITIAL_FILTRE);
  const [openForm, setOpenForm] = useState(false);
  const [excelExport, setExcelExport] = useState(0);
  const [directions, setDirections] = useState([]);
  const [services, setServices] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [openDialogueExportFiche, setOpenDialogueExportFiche] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewEvaluationRow, setPreviewEvaluationRow] = useState(null);
  const { showToast, Toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState({
    directions: false,
    services: false,
    sessions: false,
  });

  const [stats, setStats] = useState(DEFAULT_STATS);
  const [directionStats, setDirectionStats] = useState([]);
  const [kpiData, setKpiData] = useState({});
  const [classificationRows, setClassificationRows] = useState([]);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showAllPersonnel, setShowAllPersonnel] = useState(false);

  const [showFilters, setShowFilters] = useState(true);
  const [showStats, setShowStats] = useState(true);
  const [showDirectionStats, setShowDirectionStats] = useState(true);
  const [showTable, setShowTable] = useState(true);

  /* États spécifiques au tableau */
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] =
    useState(false);
  const [selected_person, setSelectedPerson] = useState(null);
  const [formEvaluationOpen, setFormEvaluationOpen] = useState(false);
  const [formResumeOpen, setFormResumeOpen] = useState(false);
  const [user_list, setUserList] = useState([]);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [debouncedMotCle, setDebouncedMotCle] = useState("");
  const motCleTimerRef = useRef(null);

  const contextData = useContext(userDetailContext);
  const location = useLocation();
  const isAllTeams = title === "Liste de toutes mes équipes";
  const isResultsPage = title === "Resultat de l'evaluation";

  useEffect(() => {
    if (isResultsPage) setShowAllPersonnel(false);
  }, [isResultsPage]);

  const handleDialogChoixFicheEvaluationOpen = (selectedUser) => {
    setSelectedUser(selectedUser);
    setOpenDialogueExportFiche(true);
  };

  const handleCloserExportFiche = () => {
    setOpenDialogueExportFiche(false);
  };

  const handleOpenEvaluationPreview = useCallback((row) => {
    if (Number(row?.candidat_id) <= 0 || Number(row?.ID_SAISON) <= 0) {
      return;
    }

    setPreviewEvaluationRow(row);
    setIsPreviewOpen(true);
  }, []);

  const handleCloseEvaluationPreview = useCallback(() => {
    setIsPreviewOpen(false);
    setPreviewEvaluationRow(null);
  }, []);

  /* -------------------- Chargement des listes -------------------- */
  const handleFetchDirectionList = useCallback(async () => {
    setLoading((l) => ({ ...l, directions: true }));
    try {
      const res = await PlanningMissionService.getDirection();
      if (res?.data) setDirections(res.data);
    } finally {
      setLoading((l) => ({ ...l, directions: false }));
    }
  }, []);

  const handleFetchServicesList = useCallback(async (direction) => {
    if (!direction?.direction_id) {
      setServices([]);
      return;
    }
    setLoading((l) => ({ ...l, services: true }));
    try {
      const res = await evaluation_utilitises_service.getServiceList(
        direction.direction_id,
      );
      if (res?.data) setServices(res.data);
    } finally {
      setLoading((l) => ({ ...l, services: false }));
    }
  }, []);

  const handleFetchSaisonList = useCallback(async () => {
    setLoading((l) => ({ ...l, sessions: true }));
    try {
      const res = await evaluation_refonte_saison_service.getList({
        mot_cle: "",
      });
      if (res) setSessions(res.data);
    } finally {
      setLoading((l) => ({ ...l, sessions: false }));
    }
  }, []);

  useEffect(() => {
    handleFetchDirectionList();
    handleFetchSaisonList();
  }, [handleFetchDirectionList, handleFetchSaisonList]);

  /* -------------------- Chargement des stats -------------------- */
  const handleFetchStats = useCallback(
    async (_filtre) => {
      setStatsLoading(true);
      try {
        const res = await evaluation_utilitises_service.getStats({
          ..._filtre,
          all_active: isResultsPage ? 1 : undefined,
        });
        const raw = res?.data ?? {};

        const nbrpersonne = Number(raw.nbrpersonne) || 0;
        const nbrevaluation = Number(raw.nbrevaluation) || 0;
        const finievaluation = Number(raw.finievaluation) || 0;

        setDirectionStats(mapDirectionStats(raw.direction ?? []));

        let classificationPct = {
          faible: 0,
          aAmeliorer: 0,
          satisfaisant: 0,
          superieur: 0,
          exceptionnel: 0,
        };

        const populationIds = new Set((raw.team_user_ids ?? []).map(Number));
        let populationResults = [];
        if (raw.active_saison_id && populationIds.size > 0) {
          try {
            const listRes =
              await evaluation_refonte_saison_service.getResultList({
                ..._filtre,
                session: { ID_SAISON: raw.active_saison_id },
                completed_evaluation_only: 1,
                // auto_evaluation_only: isAllTeams ? 1 : undefined,
              });
            const resultRows = listRes?.data ?? [];
            populationResults = isResultsPage
              ? resultRows
              : resultRows.filter((item) =>
                  populationIds.has(Number(item.candidat_id ?? item.user_id)),
                );
            classificationPct = computeClassificationStats(populationResults);
          } catch (err) {
            console.error("Erreur classification:", err);
          }
        }
        setKpiData(raw);
        setClassificationRows(populationResults);

        setStats({
          ...DEFAULT_STATS,
          total: nbrpersonne,
          autoEvaluationDone: nbrevaluation,
          evaluationDirecteurDone: finievaluation,
          autoEvaluationPending: Number(raw.autoEvaluationPending) || 0,
          ...classificationPct,
        });
      } catch (error) {
        console.error("Erreur stats:", error);
        setStats(DEFAULT_STATS);
        setDirectionStats([]);
      } finally {
        setStatsLoading(false);
      }
    },
    [isAllTeams, isResultsPage],
  );

  useEffect(() => {
    handleFetchStats(filtre);
  }, [filtre, handleFetchStats]);

  /* -------------------- Gestion des filtres -------------------- */
  const handlechangeFiltre = (evt) => {
    const { name, value } = evt.target;
    if (name === "mot_cle") {
      // Mettre à jour l'affichage immédiatement
      setFiltre((f) => ({ ...f, [name]: value }));
      // Débouncer l'appel API
      if (motCleTimerRef.current) clearTimeout(motCleTimerRef.current);
      motCleTimerRef.current = setTimeout(() => {
        setDebouncedMotCle(value);
      }, 400);
    } else {
      setFiltre((f) => ({ ...f, [name]: value }));
    }
  };

  const handleRefreshTable = () =>
    setFiltre((f) => ({ ...f, refresh: f.refresh + 1 }));

  const handleChangeAutoCompleteDirectionFilter = (new_val) => {
    setFiltre((f) => ({ ...f, direction: new_val, service: {} }));
    handleFetchServicesList(new_val);
  };

  const handleChangeAutoCompleteServiceFilter = (new_val) =>
    setFiltre((f) => ({ ...f, service: new_val }));

  const handleChangeAutoCompleteSaisonFilter = (new_val) =>
    setFiltre((f) => ({ ...f, session: new_val }));

  const handleResetFilters = () => {
    setFiltre({ ...INITIAL_FILTRE, refresh: filtre.refresh + 1 });
    setDebouncedMotCle("");
    setServices([]);
  };

  const handleSelectKpi = (key) => {
    setSelectedKpi((current) => (current === key ? null : key));
    setShowTable(true);
  };

  /* -------------------- Export Excel -------------------- */
  const downloadExcel = async () => {
    setIsExporting(true);
    try {
      const response = await evaluation_utilitises_service.exportexcel({
        ...filtre,
        is_mes_equipe: -1,
      });
      if (!response?.data) return;

      if (response.data.type === "application/json") {
        const text = await response.data.text();
        const json = JSON.parse(text);
        alert(json.message || "Erreur lors de la génération du fichier");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      let filename = `evaluationresult_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      const disposition =
        response.headers && response.headers["content-disposition"];
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExcelExport((n) => n + 1);
    } catch (error) {
      console.error("Erreur téléchargement:", error);
      alert("Erreur lors du téléchargement du fichier");
    } finally {
      setIsExporting(false);
    }
  };

  const activeFilters = useMemo(() => {
    let n = 0;
    if (filtre.direction?.direction_id) n++;
    if (filtre.service?.service_id) n++;
    if (!isAllTeams && filtre.session?.ID_SAISON) n++;
    if (filtre.mot_cle?.trim()) n++;
    return n;
  }, [filtre, isAllTeams]);

  /* -------------------- Actions tableau -------------------- */
  const handleStartEvaluation = (employee) => {
    setSelectedPerson(employee);
    setOpenForm(true);
    setFormEvaluationOpen(true);
  };

  const handleShowEvaluationResume = (employee) => {
    setSelectedPerson(employee);
    setOpenForm(true);
    setFormResumeOpen(true);
  };

  const handleDownloadFicheEvaluation = (
    id_candidat,
    id_saison,
    type_reponse,
    nom_fichier,
  ) => {
    const key = `${id_candidat}-${type_reponse}`;
    setDownloadingKey(key);

    evaluation_refonte_saison_service
      .downloadFicheEvaluation(
        id_candidat,
        id_saison,
        type_reponse,
        nom_fichier,
      )
      .catch((error) => {
        showToast(
          "error",
          error?.message || "Impossible de télécharger la fiche d'évaluation",
        );
      })
      .finally(() => setDownloadingKey(null));
  };

  /* -------------------- Colonnes -------------------- */
  const columns1 = useMemo(
    () => [
      {
        field: "candidat_name",
        headerName: "Nom et prénoms de l'employé",
        width: 240,
      },
      { field: "function_name", headerName: "Fonction", width: 220 },
      { field: "service_code", headerName: "Service", width: 110 },
      {
        field: "sup_f_name",
        headerName: "Supérieur hiérarchique",
        width: 230,
      },
      {
        field: "superieur_fonction",
        headerName: "Supérieur fonction",
        width: 180,
      },
      {
        field: "evaluee",
        headerName: "Évalué",
        width: 110,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={value ?? "N/A"}
            color={
              String(value).toUpperCase() === "OUI"
                ? "success"
                : String(value).toUpperCase() === "NON"
                  ? "warning"
                  : "default"
            }
            variant="outlined"
          />
        ),
      },
      {
        field: "evaluation_date",
        headerName: "Date d'évaluation",
        width: 180,
      },
      {
        field: "libelle_actions",
        headerName: "Actions",
        width: 240,
        renderCell: ({ row }) => (
          <Grid container spacing={1} wrap="nowrap">
            <Grid item>
              <Tooltip title="Cliquer pour commencer l'évaluation">
                <LoadingButton
                  onClick={() => handleStartEvaluation(row)}
                  color="primary"
                  variant="outlined"
                  disabled={row.evaluee !== "Oui"}
                  size="small"
                  startIcon={
                    row.evaluee_par_moi !== "Oui" ? (
                      <PsychologyAltOutlinedIcon />
                    ) : (
                      <ListAltOutlined />
                    )
                  }
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {row.evaluee_par_moi !== "Oui" ? "Evalueraaa" : "Afficherzz"}
                </LoadingButton>
              </Tooltip>
            </Grid>
            <Grid item sx={{ display: "none" }}>
              <Tooltip title="Cliquer pour afficher l'évaluation">
                <LoadingButton
                  onClick={() => handleShowEvaluationResume(row)}
                  color="secondary"
                  variant="outlined"
                  size="small"
                  startIcon={<RemoveRedEyeOutlined />}
                >
                  Afficher
                </LoadingButton>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title="Cliquer pour afficher l'évaluation">
                <LoadingButton
                  onClick={() => handleDialogChoixFicheEvaluationOpen(row)}
                  color="secondary"
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadOutlined />}
                >
                  Télécharger Fiche
                </LoadingButton>
              </Tooltip>
            </Grid>
          </Grid>
        ),
      },
    ],
    [],
  );

  const columns2 = useMemo(
    () => [
      { field: "direction_code", headerName: "Direction", width: 110 },
      { field: "service_code", headerName: "Service", width: 110 },
      { field: "registration_number", headerName: "Matricule", width: 120 },
      { field: "FULL_NAME", headerName: "Nom et prénoms", width: 240 },
      { field: "function_name", headerName: "Fonction", width: 220 },
      { field: "SAISON_NAME", headerName: "Saison", width: 160 },
      { field: "section_1", headerName: "Note section 1", width: 120 },
      { field: "section_2", headerName: "Note section 2", width: 120 },
      { field: "NOTE_GENERALE", headerName: "Note générale", width: 120 },
      { field: "CLASSIFICATION", headerName: "Classification", width: 140 },
      {
        field: "dernier_evaluateur",
        headerName: "Dernier évaluateur",
        width: 220,
      },
      {
        field: "DATE_EVALUATION",
        headerName: "Date d'évaluation",
        width: 180,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 330,
        renderCell: ({ row }) => (
          <Grid container spacing={1} wrap="nowrap">
            <Grid item>
              <Tooltip title="Afficher l'évaluation">
                <LoadingButton
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenEvaluationPreview(row);
                  }}
                  color="primary"
                  variant="outlined"
                  size="small"
                  startIcon={<RemoveRedEyeOutlined />}
                >
                  Afficher
                </LoadingButton>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title="Cliquer pour télécharger la fiche d'évaluation">
                <LoadingButton
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDialogChoixFicheEvaluationOpen(row);
                  }}
                  color="primary"
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadOutlined />}
                >
                  Télécharger
                </LoadingButton>
              </Tooltip>
            </Grid>
          </Grid>
        ),
      },
    ],
    [handleOpenEvaluationPreview],
  );

  const [columns, setColumns] = useState(columns1);

  const gridColumns = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        renderCell: (params) =>
          column.renderCell ? (
            column.renderCell({
              ...params,
              value: params.value ?? "N/A",
            })
          ) : (
            <TextCell value={params.value} />
          ),
      })),
    [columns],
  );

  const gridRows = useMemo(
    () =>
      user_list
        .filter(
          (row) =>
            !isResultsPage ||
            showAllPersonnel ||
            (row.ID_SAISON !== null &&
              row.ID_SAISON !== undefined &&
              row.ID_SAISON !== ""),
        )
        .map((row, index) => ({
          ...row,
          __employeeGridId:
            row.user_id ??
            row.candidat_id ??
            row.ID_REPONSE ??
            row.ID_SAISON ??
            `employee-${index}`,
        })),
    [user_list, showAllPersonnel, isResultsPage],
  );

  /* -------------------- Filtre effectif (mot_cle débouncé) -------------------- */
  const filtreEffectif = useMemo(
    () => ({ ...filtre, mot_cle: debouncedMotCle }),
    [filtre, debouncedMotCle],
  );

  // Ref toujours à jour — évite les stale closures dans useCallback
  const filtreEffectifRef = useRef(filtreEffectif);
  useEffect(() => {
    filtreEffectifRef.current = filtreEffectif;
  }, [filtreEffectif]);

  /* -------------------- Chargement des données tableau -------------------- */
  const handleRefreshSubordinateList = (_filtre) => {
    setOpenLoadingSimpleBackdrop(true);
    UserService.getAllOfMySubordinate(_filtre)
      .then((res) => setUserList(res?.data || []))
      .finally(() => setOpenLoadingSimpleBackdrop(false));
  };

  const handleRefreshColleagueList = (_filtre) => {
    setOpenLoadingSimpleBackdrop(true);
    UserService.getAllOfMyColleaguesInSameService(_filtre)
      .then((res) => setUserList(res?.data || []))
      .finally(() => setOpenLoadingSimpleBackdrop(false));
  };

  const handleRefreshRsultList = (_filtre) => {
    setOpenLoadingSimpleBackdrop(true);
    evaluation_refonte_saison_service
      .getResultList(_filtre)
      .then((res) => setUserList(res?.data || []))
      .finally(() => setOpenLoadingSimpleBackdrop(false));
  };

  const handleRefreshPrivilegeResultatEvaluation = () => {
    if (
      location?.pathname === "/resultats" &&
      !hasPrivilege(contextData.privileges, "evaluation_result")
    ) {
      window.location.href = "/";
    }
  };

  const refreshDataTable = useCallback(
    (_filtre) => {
      // Utilise le filtre passé en paramètre ou la ref (toujours à jour)
      const currentFiltre = _filtre ?? filtreEffectifRef.current;
      const t = String(title || "");
      if (t.includes("Liste des employées de même superieur")) {
        handleRefreshColleagueList(currentFiltre);
        setColumns(columns1);
        return;
      }
      if (t.includes("Resultat de l'evaluation")) {
        handleRefreshRsultList(currentFiltre);
        handleRefreshPrivilegeResultatEvaluation();
        setColumns(columns2);
        return;
      }
      handleRefreshSubordinateList(currentFiltre);
      setColumns(columns1);
    },
    [title, columns1, columns2],
  );

  useEffect(() => {
    refreshDataTable(filtreEffectif);
    if (openForm) setFormEvaluationOpen(openForm);
  }, [filtreEffectif, openForm, title, refreshDataTable]);

  /* -------------------- Fermeture des dialogues -------------------- */
  const handleCloseFormEvaluationDIalog = () => {
    setOpenForm(false);
    refreshDataTable();
    setFormEvaluationOpen(false);
  };

  const handleCloseFormResumeDIalog = () => {
    setOpenForm(false);
    refreshDataTable();
    setFormResumeOpen(false);
  };

  /* -------------------- Rendu -------------------- */
  return (
    <>
      <LoadingSimpleBackdrop
        openLoadingSimpleBackdrop={openLoadingSimpleBackdrop}
      />
      <DialogChoixFicheEvaluation
        open={openDialogueExportFiche}
        onClose={handleCloserExportFiche}
        idCandidat={
          isResultsPage ? selectedUser?.candidat_id : selectedUser?.user_id
        }
        shouldShowAutoEvaluationLabel={isResultsPage}
        // idSaisonActive={activeSaison?.ID_SAISON}
      />
      <EvaluationPreviewDrawer
        open={isPreviewOpen}
        evaluationRow={previewEvaluationRow}
        onClose={handleCloseEvaluationPreview}
      />
      <EvaluationExaminationForm
        candidat={selected_person}
        isOpen={formEvaluationOpen}
        handleClose={handleCloseFormEvaluationDIalog}
      />

      <EvaluationExaminationFormResume
        selected_employee={selected_person}
        isOpen={formResumeOpen}
        handleClose={handleCloseFormResumeDIalog}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          bgcolor: "transparent",
          pb: 2,
        }}
      >
        {/* En-tête avec titre et boutons de contrôle */}
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600} sx={{ color: "#1a73e8" }}>
            {title}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            {isResultsPage && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAllPersonnel}
                    onChange={(event) =>
                      setShowAllPersonnel(event.target.checked)
                    }
                    size="small"
                  />
                }
                label="Afficher tous les personnels"
                sx={{ mr: 1, "& .MuiFormControlLabel-label": { fontSize: 13 } }}
              />
            )}

            {activeFilters > 0 && (
              <Chip
                size="small"
                label={`${activeFilters} filtre${activeFilters > 1 ? "s" : ""}`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}

            <Tooltip
              title={
                showFilters ? "Masquer les filtres" : "Afficher les filtres"
              }
            >
              <Button
                size="small"
                variant={showFilters ? "outlined" : "text"}
                color="inherit"
                startIcon={<FilterAltOutlined />}
                onClick={() => setShowFilters((v) => !v)}
                sx={{ textTransform: "none" }}
              >
                Filtres
              </Button>
            </Tooltip>

            <Tooltip
              title={showStats ? "Masquer les stats" : "Afficher les stats"}
            >
              <Button
                size="small"
                variant={showStats ? "outlined" : "text"}
                color="inherit"
                startIcon={<InsightsOutlined />}
                onClick={() => setShowStats((v) => !v)}
                sx={{ textTransform: "none" }}
              >
                Statistiques
              </Button>
            </Tooltip>

            <Tooltip
              title={
                showDirectionStats
                  ? "Masquer les stats par direction"
                  : "Afficher les stats par direction"
              }
            >
              <Button
                size="small"
                variant={showDirectionStats ? "outlined" : "text"}
                color="inherit"
                startIcon={<BusinessOutlined />}
                onClick={() => setShowDirectionStats((v) => !v)}
                sx={{ textTransform: "none" }}
              >
                Par direction
              </Button>
            </Tooltip>

            <Tooltip
              title={showTable ? "Masquer le tableau" : "Afficher le tableau"}
            >
              <Button
                size="small"
                variant={showTable ? "outlined" : "text"}
                color="inherit"
                startIcon={
                  showTable ? <VisibilityOffOutlined /> : <VisibilityOutlined />
                }
                onClick={() => setShowTable((v) => !v)}
                sx={{ textTransform: "none" }}
              >
                Tableau
              </Button>
            </Tooltip>
          </Stack>
        </Box>

        {/* Filtres */}
        <Collapse in={showFilters} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: "#f8fafc",
              borderRadius: 2,
              border: "1px solid #e2e8f0",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              flexWrap="wrap"
              useFlexGap
            >
              <AutocompleteCombobox
                fullWidth
                stylex={{ minWidth: 140, flex: "1 1 140px" }}
                limitTags={2}
                label="Direction"
                class_name="filtre_list"
                loading={loading.directions}
                data={adaptDataValueToAutocompleteData(
                  directions,
                  "code",
                  "direction_id",
                )}
                valeur={
                  filtre.direction?.direction_id ? filtre.direction : null
                }
                onChange={handleChangeAutoCompleteDirectionFilter}
              />
              <AutocompleteCombobox
                fullWidth
                stylex={{ minWidth: 160, flex: "1 1 160px" }}
                limitTags={2}
                label="Service"
                class_name="filtre_list"
                loading={loading.services}
                data={adaptDataValueToAutocompleteData(
                  services,
                  "code",
                  "service_id",
                )}
                valeur={filtre.service?.service_id ? filtre.service : null}
                onChange={handleChangeAutoCompleteServiceFilter}
              />
              {!isAllTeams && (
                <AutocompleteCombobox
                  fullWidth
                  stylex={{ minWidth: 180, flex: "1 1 180px" }}
                  limitTags={2}
                  label="Session"
                  class_name="filtre_list"
                  loading={loading.sessions}
                  data={adaptDataValueToAutocompleteData(
                    sessions,
                    "SAISON_NAME",
                    "ID_SAISON",
                  )}
                  valeur={filtre.session?.ID_SAISON ? filtre.session : null}
                  onChange={handleChangeAutoCompleteSaisonFilter}
                />
              )}
              <TextField
                sx={{ minWidth: 220, flex: "1 1 220px" }}
                size="small"
                value={filtre.mot_cle}
                name="mot_cle"
                label="Mot clé"
                placeholder="Rechercher..."
                onChange={handlechangeFiltre}
                className="filtre_list"
              />
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              flexWrap="wrap"
              useFlexGap
            >
              <Tooltip title="Réinitialiser les filtres">
                <span>
                  <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    startIcon={<ClearOutlined />}
                    onClick={handleResetFilters}
                    disabled={activeFilters === 0}
                  >
                    Réinitialiser
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Actualiser la liste">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshOutlined />}
                  onClick={handleRefreshTable}
                >
                  Actualiser
                </Button>
              </Tooltip>

              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={downloadExcel}
                disabled={isExporting}
                startIcon={
                  isExporting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DownloadOutlined />
                  )
                }
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {isExporting ? "Export..." : "Excel export"}
              </Button>
            </Stack>
          </Box>
        </Collapse>

        {/* Statistiques */}
        <Collapse
          in={showStats || showDirectionStats}
          timeout="auto"
          unmountOnExit
        >
          <StatsAndDirectionPanel
            stats={showStats ? stats : DEFAULT_STATS}
            directions={showDirectionStats ? directionStats : []}
            loading={statsLoading}
            onSelectKpi={isAllTeams ? handleSelectKpi : undefined}
            selectedKpi={selectedKpi}
          />
        </Collapse>

        {/* Tableau */}
        <Collapse in={showTable} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2 }}>
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                overflow: "hidden",
              }}
            >
              <DataGrid
                rows={gridRows}
                columns={gridColumns}
                getRowId={(row) => row.__employeeGridId}
                onRowClick={
                  isResultsPage
                    ? ({ row }) => handleOpenEvaluationPreview(row)
                    : undefined
                }
                autoHeight
                getRowHeight={() => "auto"}
                estimatedRowHeight={68}
                pageSizeOptions={[5, 10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { page: 0, pageSize: 10 } },
                }}
                disableRowSelectionOnClick
                localeText={{ noRowsLabel: "Aucun employé à afficher" }}
                sx={{
                  minWidth: 1000,
                  border: 0,
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#e3f2fd",
                    color: "#0d47a1",
                    borderBottom: "2px solid #90caf9",
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor: "#e3f2fd",
                    borderRight: "1px solid #bbdefb",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: 13,
                    fontWeight: 700,
                    whiteSpace: "normal",
                    lineHeight: 1.25,
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: 13,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    borderRight: "1px solid #e2e8f0",
                    borderBottom: "1px solid #eef2f7",
                    py: 1,
                    lineHeight: 1.4,
                  },
                  "& .MuiDataGrid-row:nth-of-type(odd)": {
                    backgroundColor: "#f8fafc",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#eef6ff",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "1px solid #e2e8f0",
                    bgcolor: "#fff",
                  },
                }}
              />
            </Paper>
          </Box>
        </Collapse>
      </Box>

      <Toast />
    </>
  );
};

export default EmployeeListTable;
